import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const GOLD = '#B8860B';

export default function BarberView() {
  const [code, setCode] = useState('');
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const navigate = useNavigate();

  /**
   * Security Check: Lockout Persistence
   */
  useEffect(() => {
    const lockUntil = localStorage.getItem('lock_until');
    if (lockUntil && Date.now() < parseInt(lockUntil)) {
      setIsLocked(true);
      // Optional: Set a timeout to unlock automatically when time expires
      const remaining = parseInt(lockUntil) - Date.now();
      setTimeout(() => setIsLocked(false), remaining);
    }
  }, []);

  /**
   * Brute Force Protection Logic
   */
  const handleFailedAttempt = () => {
    const attempts = parseInt(localStorage.getItem('brute_attempts') || '0') + 1;
    localStorage.setItem('brute_attempts', attempts);
    
    if (attempts >= 5) {
      const lockTime = Date.now() + (15 * 60 * 1000); // 15-minute lock
      localStorage.setItem('lock_until', lockTime);
      setIsLocked(true);
      toast.error("Security Lock: Too many failed attempts. Try again in 15 mins.");
    } else {
      toast.error(`Invalid Code. ${5 - attempts} attempts remaining.`);
    }
  };

  /**
   * Code Verification & Access
   */
  const handleAccess = async (e) => {
    e.preventDefault();
    if (isLocked) return toast.error("Too many attempts. Access temporarily disabled.");
    if (code.length !== 6) return toast.error("Enter a 6-digit code");

    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'share_codes', code));
      
      if (snap.exists()) {
        const data = snap.data();
        
        // Expiry Validation
        if (data.expiresAt.toDate() < new Date()) {
          toast.error("This code has expired.");
        } else {
          setClientData(data);
          // Success Path: Clear security flags
          localStorage.removeItem('brute_attempts');
          localStorage.removeItem('lock_until');
          
          // System Analytics: Log successful access
          await updateDoc(doc(db, 'system', 'stats'), { 
            viralSharesAccessed: increment(1) 
          });
        }
      } else {
        handleFailedAttempt();
      }
    } catch (err) {
      toast.error("Access denied");
      console.error("Access Error:", err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * VIEW: CLIENT LOOKBOOK (THE VAULT)
   */
  if (clientData) {
    return (
      <div style={viewerContainer}>
        <header style={vHeader}>
          <div>
            <h2 style={{ color: GOLD, margin: 0, fontSize: '1.5rem', letterSpacing: '1px' }}>
              {clientData.userName.toUpperCase()}'S VAULT
            </h2>
            <p style={{ margin: 0, fontSize: '0.7rem', opacity: 0.5 }}>AUTHORIZED BARBER ACCESS</p>
          </div>
          <button onClick={() => setClientData(null)} style={exitBtn}>EXIT VAULT</button>
        </header>

        <div style={vGrid}>
          {clientData.lookbook && clientData.lookbook.map((style, i) => (
            <div key={i} style={vCard}>
              <img src={style.imageUrl || '/icons/default-hair.png'} style={vImg} alt={style.name} />
              <div style={vInfo}>
                <h3 style={{ margin: 0, color: GOLD }}>{style.name}</h3>
                <p style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '15px' }}>
                  AI Scan Visualization
                </p>
                <button 
                  onClick={() => navigate(`/viewer/${style.styleId}`)} 
                  style={v3DBtn}
                >
                  OPEN 3D VIEW
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /**
   * VIEW: ENTRY PIN PAD
   */
  return (
    <div style={entryContainer}>
      <div style={formCard}>
        <h1 style={{ color: GOLD, letterSpacing: '2px', marginBottom: '10px' }}>
          ESDRAS <span style={{ color: '#fff' }}>BARBER ACCESS</span>
        </h1>

        {isLocked ? (
          <div style={{ padding: '20px' }}>
            <p style={{ color: '#ff6b6b', fontWeight: 'bold' }}>SECURITY LOCK ACTIVE</p>
            <p style={{ opacity: 0.6, fontSize: '0.8rem' }}>
              Too many incorrect attempts. Please try again in 15 minutes.
            </p>
          </div>
        ) : (
          <>
            <p style={{ opacity: 0.6, marginBottom: '30px' }}>
              Enter the 6-digit code provided by your client to view their lookbook.
            </p>
            <form onSubmit={handleAccess}>
              <input 
                type="text" 
                placeholder="000000" 
                maxLength={6} 
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                style={inputStyle}
                autoFocus
              />
              <button 
                type="submit" 
                style={accessBtn} 
                disabled={loading}
              >
                {loading ? 'VERIFYING...' : 'VIEW LOOKBOOK'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// --- STYLES ---

const entryContainer = { 
  height: '100vh', 
  background: '#000', 
  display: 'flex', 
  alignItems: 'center', 
  justifyContent: 'center', 
  padding: '20px' 
};

const formCard = { 
  textAlign: 'center', 
  background: '#050a0f', 
  padding: '40px', 
  borderRadius: '30px', 
  border: `1px solid ${GOLD}`, 
  maxWidth: '450px', 
  width: '100%',
  boxShadow: '0 20px 50px rgba(0,0,0,0.5)'
};

const inputStyle = { 
  width: '100%', 
  background: 'rgba(255,255,255,0.05)', 
  border: `1px solid ${GOLD}`, 
  padding: '20px', 
  borderRadius: '15px', 
  color: '#fff', 
  fontSize: '2rem', 
  textAlign: 'center', 
  letterSpacing: '10px', 
  marginBottom: '20px',
  outline: 'none'
};

const accessBtn = { 
  width: '100%', 
  padding: '18px', 
  background: GOLD, 
  color: '#000', 
  border: 'none', 
  borderRadius: '15px', 
  fontWeight: '900', 
  cursor: 'pointer',
  transition: 'transform 0.2s',
  letterSpacing: '1px'
};

const viewerContainer = { 
  minHeight: '100vh', 
  background: '#000', 
  padding: '20px' 
};

const vHeader = { 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center', 
  padding: '10px 0 25px', 
  borderBottom: '1px solid rgba(255,255,255,0.1)', 
  marginBottom: '30px',
  maxWidth: '1200px',
  margin: '0 auto 30px'
};

const exitBtn = { 
  background: 'rgba(255,255,255,0.1)', 
  color: '#fff', 
  border: 'none', 
  padding: '10px 20px', 
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '0.8rem'
};

const vGrid = { 
  display: 'grid', 
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
  gap: '25px', 
  maxWidth: '1200px', 
  margin: '0 auto' 
};

const vCard = { 
  background: '#050a0f', 
  borderRadius: '24px', 
  overflow: 'hidden', 
  border: '1px solid rgba(184,134,11,0.2)',
  boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
};

const vImg = { 
  width: '100%', 
  height: '320px', 
  objectFit: 'cover' 
};

const vInfo = { 
  padding: '20px', 
  textAlign: 'center' 
};

const v3DBtn = { 
  width: '100%', 
  padding: '14px', 
  background: GOLD, 
  color: '#000',
  border: 'none', 
  borderRadius: '12px', 
  fontWeight: '900',
  cursor: 'pointer',
  fontSize: '0.9rem'
};
    
